import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import useModalStore from '@/store/useModalStore';
import { OverdueReceivableResponse } from '@/services/wallet.service';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Coins,
  HelpCircle,
  Info,
  RefreshCw,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFetchOverdueReceivables } from '../hooks/useFetchOverdueReceivables';

export const PENDING_RECEIVABLES_MODAL_KEY = 'pending-receivables';

const SEEN_RECEIVABLES_COUNT_KEY = 'mundpay_seen_overdue_receivable_count';

export function getSeenReceivablesCount(): number | null {
  try {
    const stored = localStorage.getItem(SEEN_RECEIVABLES_COUNT_KEY);
    return stored ? parseInt(stored, 10) : null;
  } catch {
    return null;
  }
}

export function markReceivablesCountAsSeen(count: number) {
  try {
    localStorage.setItem(SEEN_RECEIVABLES_COUNT_KEY, count.toString());
  } catch {
    /* noop */
  }
}

interface PendingReceivablesModalProps {
  currency: string;
  onSeen: () => void;
}

export default function PendingReceivablesModal({
  currency,
  onSeen,
}: PendingReceivablesModalProps) {
  const { t } = useTranslation('wallet');
  const { modals, closeModal } = useModalStore();
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const getEntryTypeBadgeClassName = (entryType?: string) => {
    switch (entryType) {
      case 'receivable':
        return 'bg-[#A9D342]/15 text-[#A9D342] border border-[#A9D342]/30';
      case 'retention':
        return 'bg-amber-500/15 text-amber-400 border border-amber-400/30';
      case 'available':
        return 'bg-sky-500/15 text-sky-400 border border-sky-400/30';
      default:
        return 'bg-zinc-700/30 text-zinc-300 border border-zinc-600/40';
    }
  };

  const isOpen = !!modals[PENDING_RECEIVABLES_MODAL_KEY];

  const { overdueReceivables, isLoading } = useFetchOverdueReceivables({
    currency,
    currentPage,
    enabled: isOpen,
  });

  const overdueItems = overdueReceivables?.data ?? [];

  const tableData: OverdueReceivableResponse = {
    data: overdueItems,
    links: overdueReceivables?.links ?? {
      first: '',
      last: '',
      prev: null,
      next: null,
    },
    meta: overdueReceivables?.meta ?? {
      current_page: 1,
      from: 0,
      last_page: 1,
      links: [],
      path: '',
      per_page: 15,
      to: 0,
      total: 0,
    },
  };

  const totalCount = tableData.meta?.total ?? 0;

  const headers = [
    { key: 'type', title: t('pendingReceivables.tableType') },
    { key: 'sale', title: t('pendingReceivables.tableSale') },
    { key: 'date', title: t('pendingReceivables.tableDate') },
    { key: 'value', title: t('pendingReceivables.tableValue') },
  ];

  const handleClose = useCallback(() => {
    markReceivablesCountAsSeen(totalCount);
    onSeen();
    setCurrentPage(1);
    closeModal(PENDING_RECEIVABLES_MODAL_KEY);
  }, [totalCount, onSeen, closeModal]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="w-[95vw] sm:w-[90vw] max-w-3xl max-h-[85vh] overflow-hidden p-0 rounded-lg">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col max-h-[85vh] min-w-0"
          >
            <DialogHeader className="p-3 sm:p-6 pb-3 sm:pb-4 border-b border-colorBorder shrink-0 text-left">
              <div className="flex items-center gap-2 sm:gap-3 pr-6">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 shrink-0">
                  <Coins size={16} className="text-amber-400 sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <DialogTitle className="text-sm sm:text-xl font-semibold truncate">
                      {t('pendingReceivables.title')}
                    </DialogTitle>
                    {totalCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-[10px] sm:text-xs font-bold rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 shrink-0">
                        {totalCount}
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-[10px] sm:text-sm mt-0.5 truncate">
                    {t('pendingReceivables.subtitle')}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 p-3 sm:p-6 space-y-3 sm:space-y-5 min-w-0">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.05 }}
                className="flex items-center gap-2 bg-amber-500/10 rounded-lg px-2.5 sm:px-3 py-2 border border-amber-500/20"
              >
                <Clock size={14} className="text-amber-400 shrink-0" />
                <span className="text-[10px] sm:text-sm font-medium text-amber-400">
                  {t('pendingReceivables.totalPending')} {totalCount}
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-zinc-900/50 rounded-lg p-2.5 sm:p-4 border border-colorBorder min-w-0"
              >
                <div className="flex items-start gap-2">
                  <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-sky-500/10 border border-sky-500/20 shrink-0 mt-0.5">
                    <HelpCircle size={12} className="text-sky-400 sm:w-3.5 sm:h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[11px] sm:text-sm font-semibold text-colorText">
                      {t('pendingReceivables.whatIsTitle')}
                    </h3>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed mt-1">
                      {t('pendingReceivables.whatIsText')}
                    </p>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed mt-1.5">
                      {t('pendingReceivables.whatIsReason')}
                    </p>
                    <ul className="mt-1 space-y-1">
                      <li className="flex items-start gap-1.5">
                        <ChevronRight size={12} className="text-amber-400 shrink-0 mt-0.5" />
                        <span className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                          {t('pendingReceivables.whatIsReason1')}
                        </span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <ChevronRight size={12} className="text-amber-400 shrink-0 mt-0.5" />
                        <span className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                          {t('pendingReceivables.whatIsReason2')}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="bg-zinc-900/50 rounded-lg p-2.5 sm:p-4 border border-colorBorder min-w-0"
              >
                <div className="flex items-start gap-2">
                  <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 shrink-0 mt-0.5">
                    <RefreshCw size={12} className="text-emerald-500 sm:w-3.5 sm:h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[11px] sm:text-sm font-semibold text-colorText">
                      {t('pendingReceivables.howReleaseTitle')}
                    </h3>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed mt-1">
                      {t('pendingReceivables.howReleaseIntro')}
                    </p>
                    <ul className="mt-1 space-y-1">
                      <li className="flex items-start gap-1.5">
                        <div className="flex items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-emerald-500/20 shrink-0 mt-0.5">
                          <CheckCircle2 size={8} className="text-emerald-500 sm:w-2.5 sm:h-2.5" />
                        </div>
                        <span className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                          {t('pendingReceivables.howRelease1')}
                        </span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <div className="flex items-center justify-center w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-emerald-500/20 shrink-0 mt-0.5">
                          <CheckCircle2 size={8} className="text-emerald-500 sm:w-2.5 sm:h-2.5" />
                        </div>
                        <span className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                          {t('pendingReceivables.howRelease2')}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-amber-500/5 rounded-lg p-2.5 sm:p-4 border border-amber-500/20 min-w-0"
              >
                <div className="flex items-start gap-2">
                  <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 shrink-0 mt-0.5">
                    <AlertTriangle size={12} className="text-amber-400 sm:w-3.5 sm:h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[11px] sm:text-sm font-semibold text-amber-400">
                      {t('pendingReceivables.importantTitle')}
                    </h3>
                    <ul className="mt-1 space-y-1">
                      <li className="flex items-start gap-1.5">
                        <Info size={10} className="text-amber-400 mt-0.5 shrink-0 sm:w-3 sm:h-3" />
                        <span className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                          {t('pendingReceivables.important1')}
                        </span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <Info size={10} className="text-amber-400 mt-0.5 shrink-0 sm:w-3 sm:h-3" />
                        <span className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                          {t('pendingReceivables.important2')}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="min-w-0"
              >
                <h3 className="text-[11px] sm:text-sm font-semibold text-colorText flex items-center gap-2 mb-2 sm:mb-3">
                  <Coins size={14} className="text-amber-400 shrink-0" />
                  {t('pendingReceivables.detailsTitle')}
                </h3>

                <div className="overflow-x-auto -mx-3 sm:-mx-6 px-3 sm:px-6">
                  <TableListCustom
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    headers={headers}
                    data={tableData}
                    isLoadingData={isLoading}
                    emptyTitleMessage={t('pendingReceivables.empty')}
                    emptyDescriptionMessage={t('pendingReceivables.empty')}
                    emptyIconMessage={<AlertCircle className="text-muted-foreground" />}
                  >
                    {overdueItems.map((item) => (
                      <TableRow key={item.id} className="hover:bg-zinc-900/50">
                        <TableCell className="text-[10px] sm:text-sm text-colorText whitespace-nowrap py-2 sm:py-4">
                          <Badge
                            variant="secondary"
                            className={getEntryTypeBadgeClassName(item.entry_type || item.type)}
                          >
                            {(item.entry_type || item.type) === 'receivable' ? t('receivable') : (item.entry_type || item.type) === 'retention' ? t('retention') : t('available')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[10px] sm:text-sm text-colorText py-2 sm:py-4">
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{item.operation_label}</span>
                            {item.reference_uuid && (
                              <span 
                                className="text-[9px] sm:text-xs text-primary cursor-pointer hover:underline font-mono truncate max-w-[120px] sm:max-w-none"
                                onClick={() => {
                                  navigate(`/sales/sales-details/${item.reference_uuid}`);
                                  closeModal(PENDING_RECEIVABLES_MODAL_KEY);
                                }}
                              >
                                {item.reference_uuid}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-[10px] sm:text-sm text-muted-foreground whitespace-nowrap py-2 sm:py-4">
                          {item.created_at_formatted}
                        </TableCell>
                        <TableCell className="text-[10px] sm:text-sm font-semibold text-amber-400 whitespace-nowrap text-right py-2 sm:py-4">
                          {item.amount_formatted}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableListCustom>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-3 sm:p-6 pt-3 sm:pt-4 border-t border-colorBorder bg-background shrink-0"
            >
              <Button onClick={handleClose} className="w-full h-9 sm:h-10 text-xs sm:text-sm">
                <CheckCircle2 className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {t('pendingReceivables.understoodButton')}
              </Button>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
